import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from './api/baseApiConfig';
import config from '../config';

export interface ChatMessage {
	role: string;
	content: string;
	type?: string | null;
	name?: string | null;
	file_data?: any | null;
	webUrl?: string | null;
	id?: string | null;
}

export interface AITextGenerationParams {
	messages: ChatMessage[];
	model?: string;
	provider?: string;
	promptPath?: string;
	companyId: string;
	conversationId?: string;
}

export interface AITextGenerationResponse {
	// For POST /chat endpoint
	response?: string;
	content?: string; // Fallback for compatibility
	conversationId?: string;
	conversationHistory?: Array<{
		role: string;
		content: string;
		timestamp: string;
	}>;
	currentDate?: string;
	metadata?: {
		userId: string;
		chatId: string;
		conversationId: string;
		messageCount: number;
		companyId: string;
	};
	// For GET /chat/history/{id} endpoint
	messages?: Array<{
		role: string;
		content: string;
		timestamp: string;
		messageId?: string;
	}>;
	userId?: string;
	createdAt?: string;
	updatedAt?: string;
	title?: string;
}

export interface ChatHistoryResponse {
	chatContent: ChatMessage[];
	conversationId?: string;
}

export interface ConversationListItem {
	conversationId: string;
	chatId: string;
	userId: string;
	createdAt: string;
	updatedAt: string;
	messageCount: number;
}

export interface ConversationsResponse {
	conversations: ConversationListItem[];
}

export const aiTextGenerationApi = createApi({
	reducerPath: 'aiTextGenerationApi',
	baseQuery: createBaseQuery(`${config.APIM_BASE_URL}/ai`, {
		timeout: 300000, // 5 minutes timeout
	}),
	endpoints: builder => ({
		getChatHistory: builder.query<ChatHistoryResponse, { companyId: string; chatId: string }>({
			query: ({ companyId, chatId }) => ({
				url: `/chat/user/${companyId}/${chatId}`,
				method: 'GET',
			}),
		}),
		getConversationHistory: builder.query<AITextGenerationResponse, { conversationId: string }>({
			query: ({ conversationId }) => ({
				url: `/chat/history/${conversationId}`,
				method: 'GET',
			}),
		}),
		getConversations: builder.query<ConversationsResponse, { userId: string }>({
			query: ({ userId }) => ({
				url: `/chat/conversations/${userId}`,
				method: 'GET',
			}),
		}),
		deleteChat: builder.mutation<void, { companyId: string; chatId: string }>({
			query: ({ companyId, chatId }) => ({
				url: `/chat/user/${companyId}/${chatId}`,
				method: 'DELETE',
			}),
		}),
		deleteConversation: builder.mutation<void, { conversationId: string }>({
			query: ({ conversationId }) => ({
				url: `/chat/history/${conversationId}`,
				method: 'DELETE',
			}),
		}),
		generateText: builder.mutation<AITextGenerationResponse, AITextGenerationParams>({
			query: ({ messages, model, provider, promptPath, companyId, conversationId }) => {
				const params = new URLSearchParams();
				if (provider) {
					params.append('chat-provider', provider);
				}
				if (model) {
					params.append('chat-model', model);
				}
				if (promptPath) {
					params.append('promptPath', promptPath);
				}

				if (!companyId) {
					companyId = 'no-company';
				}

				return {
					url: `/chat`,
					method: 'POST',
					params: params,
					body: {
						chatId: `word-document-${companyId}`,
						companyId: companyId,
						conversationId: conversationId,
						chatContent: [
							{
								role: 'system',
								content: `You are an AI Financial Content Generator. Your sole purpose is to produce high-quality, professional financial content formatted for direct pasting into a Microsoft Word document.

								Output Format Requirements:

								Strict HTML (<div> based): All output must be enclosed within a single <div> tag.
								No extraneous HTML: Do not include <html>, <body>, <head>, <footer>, or any other page-level tags.
								Markdown to HTML Conversion: Internally, convert your structured financial expertise into valid HTML elements (e.g., <h2> for headings, <p> for paragraphs, <ul> or <ol> for lists, <table> for tabular data, <strong> for emphasis).
								Template Adherence: If a specific content template is provided in the conversation, strictly follow its structure and formatting. Template is marked by prefix 'template:'. Template might contain information and content text. Don't remove or replace by default. Use existing content and implement generated content in the current document.
								Clean Output: Ensure there are no additional markings or non-HTML text outside the main <div>. Expected output is a valid HTML document.
								
								Financial Expert Persona & Responsibilities:

								Role: You are an expert financial analyst and advisor.
								Core Objective: To generate accurate, well-researched, and insightful financial content. This includes, but is not limited to: market analyses, investment explanations, financial planning guidance, economic commentaries, and risk assessments.
								Tone & Style: Maintain a professional, authoritative, objective, and clear tone. Use precise financial terminology but explain complex concepts in an understandable manner for the intended audience (assume a generally informed, but not expert, reader unless specified).
								Knowledge Base: Access and leverage comprehensive financial data, economic principles, investment strategies, and regulatory guidelines.
								Accuracy & Objectivity: Prioritize factual accuracy and present balanced perspectives. Avoid speculative claims or definitive predictions unless explicitly supported by data and clearly qualified as such.
								Ethical Guidelines:
								Do not provide personalized financial advice. Always include disclaimers when appropriate (e.g., "This content is for informational purposes only and does not constitute financial advice.").
								Do not recommend specific securities, products, or services unless explicitly instructed to analyze a hypothetical scenario.
								Do not generate content that promotes illegal, unethical, or harmful financial practices.
								Handling Ambiguity: If a request is unclear or requires specific data not provided, you may state assumptions made or ask for clarification within the generated content, or ideally, politely request more information if contextually appropriate before generating.
								Content Generation Process:
								Understand the user's financial content request.
								Identify the core financial concepts and required data.
								Structure the information logically with appropriate headings and subheadings.
								Write clear, concise, and informative paragraphs.
								Format all content strictly as HTML within the single <div>.
								
								Always use the same language as the user's request.`,
							},
							...messages,
						],
					},
				};
			},
			transformResponse: (response: AITextGenerationResponse) => {
				// POST /chat returns 'response' field, keep other fields intact
				const contentToClean = response?.response || response?.content;

				if (contentToClean) {
					let cleanedContent = contentToClean
						// Remove markdown code block markers
						.replace(/```html\n?/g, '')
						.replace(/```\n?/g, '')
						// Remove any remaining newlines
						.replace(/\n\r\n/g, '')
						.replace(/\n/g, '')
						// Remove any potential markdown backticks
						.replace(/`/g, '')
						// Trim whitespace
						.trim();

					// Ensure the content is wrapped in a div if it isn't already
					if (!cleanedContent.startsWith('<div>')) {
						cleanedContent = `<div>${cleanedContent}</div>`;
					}

					return {
						...response,
						content: cleanedContent,
						response: cleanedContent,
					};
				}
				throw new Error('No response content received');
			},
		}),
	}),
});

export const {
	useGenerateTextMutation,
	useGetChatHistoryQuery,
	useGetConversationHistoryQuery,
	useGetConversationsQuery,
	useDeleteChatMutation,
	useDeleteConversationMutation,
} = aiTextGenerationApi;
